<template>
    <!-- Expanded, the same frame moves to <body> (so no transformed or
         overflow-hidden ancestor can clip it) and covers the whole screen;
         the ResizeObserver re-renders the page at the new size. -->
    <Teleport to="body" :disabled="!expanded">
        <div class="pdf-slides" :class="{ 'pdf-slides-expanded': expanded, 'pdf-slides-rotated': rotated }" ref="frame">
            <canvas ref="canvas" v-show="ready"></canvas>
            <div v-if="!ready && !failed" class="pdf-slides-wait"><span class="spinner-border"></span></div>
            <div v-if="failed" class="pdf-slides-wait">No se ha podido cargar la presentación.</div>
            <div v-if="pages > 1" class="pdf-slides-counter">{{ shown_page + 1 }} / {{ pages }}</div>

            <button v-if="ready && !expanded" type="button" class="pdf-slides-btn pdf-slides-btn-expand" title="Ampliar presentación" aria-label="Ampliar presentación" @click="expand">
                <i class="bi bi-arrows-angle-expand"></i>
            </button>
            <div v-if="expanded" class="pdf-slides-toolbar">
                <button v-if="is_portrait" type="button" class="pdf-slides-btn" :title="rotate ? 'Ver en vertical' : 'Girar'" :aria-label="rotate ? 'Ver en vertical' : 'Girar'" @click="rotate = !rotate">
                    <i :class="'bi ' + (rotate ? 'bi-phone' : 'bi-phone-landscape')"></i>
                </button>
                <button type="button" class="pdf-slides-btn" title="Cerrar" aria-label="Cerrar" @click="collapse">
                    <i class="bi bi-x-lg"></i>
                </button>
            </div>
        </div>
    </Teleport>
</template>

<script>
import { loadPdf } from '@/utils/pdf'
import { fullscreen_element, request_fullscreen, exit_fullscreen, fullscreen_supported } from '@/components/FullScreen.vue'

function viewport_is_portrait(){
    return window.innerHeight > window.innerWidth;
}

// A presentation (utils.ACTIVITY_TYPE.PRESENTATION): one page of the PDF at
// a time inside a 16:9 frame, the page being decided elsewhere (the room's
// game.content_page, moved by the presenter - see server.js's
// _presentation_step - or the player's own arrows online).
export default {
    name: "PdfSlides",
    emits: ['pages'],
    props: {
        url: { type: String, required: true },
        page: { type: Number, default: 0 },
    },
    data(){
        return {
            ready: false,
            failed: false,
            pages: 0,
            shown_page: 0,
            // Expanded view (see expand()).
            expanded: false,
            // Wants the slide turned sideways while the screen is portrait.
            rotate: true,
            is_portrait: viewport_is_portrait(),
        };
    },
    computed: {
        // Turn the slide 90° only while the screen itself is still portrait
        // (orientation lock unavailable - e.g. iOS - or the user keeps the
        // phone upright): a sideways 16:9 slide then fills the whole screen.
        // Once the device is actually turned to landscape it's not needed.
        rotated(){
            return this.expanded && this.rotate && this.is_portrait;
        }
    },
    watch: {
        url(){ this.open(); },
        page(){ this.render(); },
    },
    mounted(){
        this.resize_observer = new ResizeObserver(() => {
            cancelAnimationFrame(this.resize_frame);
            this.resize_frame = requestAnimationFrame(this.render);
        });
        this.resize_observer.observe(this.$refs.frame);
        window.addEventListener('resize', this.on_viewport_change);
        window.addEventListener('keydown', this.on_key);
        this.open();
    },
    beforeUnmount(){
        window.removeEventListener('resize', this.on_viewport_change);
        window.removeEventListener('keydown', this.on_key);
        if(this.expanded) this.collapse();
        this.resize_observer.disconnect();
        cancelAnimationFrame(this.resize_frame);
        this.cancel_render();
        if(this.doc) this.doc.destroy();
        this.doc = null;
    },
    methods: {
        on_viewport_change(){
            this.is_portrait = viewport_is_portrait();
        },
        on_key(event){
            if(this.expanded && event.key === 'Escape') this.collapse();
        },
        // Full-screen slide. Where the browser allows it (Android Chrome),
        // also goes real fullscreen and locks the screen to landscape - that
        // needs fullscreen first. Elsewhere the CSS rotation does the job.
        async expand(){
            this.rotate = true;
            this.expanded = true;
            this.entered_fullscreen = false;
            this.locked_orientation = false;
            if(fullscreen_supported() && !fullscreen_element()){
                await request_fullscreen(document.documentElement);
                this.entered_fullscreen = !!fullscreen_element();
            }
            try{
                if(screen.orientation && screen.orientation.lock){
                    await screen.orientation.lock('landscape');
                    this.locked_orientation = true;
                }
            }catch(ex){ /* not allowed here (iOS, desktop...) - CSS rotation instead */ }
            this.on_viewport_change();
        },
        collapse(){
            this.expanded = false;
            if(this.locked_orientation && screen.orientation && screen.orientation.unlock){
                try{ screen.orientation.unlock(); }catch(ex){ /* ignore */ }
            }
            this.locked_orientation = false;
            if(this.entered_fullscreen) exit_fullscreen();
            this.entered_fullscreen = false;
        },
        async open(){
            this.cancel_render();
            if(this.doc) this.doc.destroy();
            this.doc = null;
            this.ready = false;
            this.failed = false;
            const url = this.url;
            try{
                const doc = await loadPdf(url);
                if(url != this.url || !this.$refs.frame){ doc.destroy(); return; }
                this.doc = doc;
                this.pages = doc.numPages;
                this.$emit('pages', doc.numPages);
                await this.render();
            }catch(ex){
                console.warn('No se ha podido cargar el PDF:', ex);
                this.failed = true;
            }
        },
        cancel_render(){
            if(this.task) this.task.cancel();
            this.task = null;
        },
        // Fits the whole page inside the frame, drawn at the screen's real
        // pixel density so text stays crisp on a projector or a phone.
        async render(){
            const doc = this.doc;
            const frame = this.$refs.frame;
            if(!doc || !frame) return;
            // Page turns can arrive faster than pages load - only the
            // latest request gets drawn.
            const seq = this.render_seq = (this.render_seq || 0) + 1;
            const number = Math.min(Math.max(0, this.page), doc.numPages - 1);
            const page = await doc.getPage(number + 1);
            if(doc !== this.doc || seq !== this.render_seq) return;

            const base = page.getViewport({ scale: 1 });
            const scale = Math.min(frame.clientWidth / base.width, frame.clientHeight / base.height) || 1;
            const ratio = window.devicePixelRatio || 1;
            const viewport = page.getViewport({ scale: scale * ratio });
            const canvas = this.$refs.canvas;

            this.cancel_render();
            // Drawn offscreen first: swapping a finished bitmap in avoids
            // flashing a blank slide on every page turn.
            const buffer = document.createElement('canvas');
            buffer.width = Math.floor(viewport.width);
            buffer.height = Math.floor(viewport.height);
            const task = page.render({ canvasContext: buffer.getContext('2d'), viewport });
            this.task = task;
            try{
                await task.promise;
            }catch(ex){
                if(ex && ex.name == 'RenderingCancelledException') return;
                throw ex;
            }
            if(this.task !== task) return;
            this.task = null;

            canvas.width = buffer.width;
            canvas.height = buffer.height;
            canvas.style.width = Math.floor(viewport.width / ratio) + 'px';
            canvas.style.height = Math.floor(viewport.height / ratio) + 'px';
            canvas.getContext('2d').drawImage(buffer, 0, 0);
            this.shown_page = number;
            this.ready = true;
        }
    }
}
</script>

<style scoped>
    /* 16:9 whatever the screen: as wide as it can be while still fitting
       70vh of height. A slide with another ratio is letterboxed inside. */
    .pdf-slides{
        position: relative;
        width: min(100%, calc(70vh * 16 / 9));
        aspect-ratio: 16 / 9;
        margin: 0 auto;
        border-radius: 8px;
        background-color: #111;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    }
    .pdf-slides canvas{
        background-color: #fff;
    }
    .pdf-slides-wait{
        font-family: "Archivo", sans-serif;
        font-weight: 700;
        text-align: center;
    }
    /* Expanded: covers the whole screen, above everything else. */
    .pdf-slides-expanded{
        position: fixed;
        inset: 0;
        z-index: 3000;
        width: 100vw;
        height: 100vh;
        height: 100dvh;
        max-width: none;
        aspect-ratio: auto;
        margin: 0;
        border-radius: 0;
        background-color: #000;
    }
    /* Portrait screen: lay the frame out landscape (width = screen height)
       and turn it 90° into place. */
    .pdf-slides-rotated{
        inset: auto;
        top: 50%;
        left: 50%;
        width: 100vh;
        width: 100dvh;
        height: 100vw;
        transform: translate(-50%, -50%) rotate(90deg);
    }

    .pdf-slides-btn{
        width: 40px;
        height: 40px;
        border: 0;
        border-radius: 50%;
        background-color: rgba(0, 0, 0, 0.6);
        color: #fff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 1.1rem;
        touch-action: manipulation;
    }
    .pdf-slides-btn-expand{
        position: absolute;
        left: 12px;
        bottom: 12px;
    }
    .pdf-slides-toolbar{
        position: absolute;
        top: 12px;
        right: 12px;
        display: flex;
        gap: 10px;
    }

    .pdf-slides-counter{
        position: absolute;
        right: 12px;
        bottom: 12px;
        padding: 2px 10px;
        border-radius: 999px;
        background-color: rgba(0, 0, 0, 0.6);
        color: #fff;
        font-family: "Archivo", sans-serif;
        font-weight: 700;
        font-size: 0.85rem;
    }
</style>
